import { useMemo } from 'react';
import Select from 'react-select';
import styled from '@emotion/styled';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { withSidePanelStyles } from './Pill';
import * as Presentation from './Presentation';

export type EntityOption = {
  value: string;
  label: string;
};

type Props = {
  label: string;
  placeholder: string;
  options: EntityOption[];
  value: EntityOption | null;
  isLoading?: boolean;
  /** The options failed to load; say so instead of claiming there are none. */
  isError?: boolean;
  disabled?: boolean;
  isSidePanel?: boolean;
  onChange: (option: EntityOption | null) => void;
};

/** Single-select filter row for picking one project entity (a segment or a patient journey). */
export function EntitySelect({
  label,
  placeholder,
  options,
  value,
  isLoading,
  isError,
  disabled,
  isSidePanel,
  onChange,
}: Props) {
  const theme = useTheme();
  const noun = `${label.toLowerCase()}s`;

  const noOptionsMessage = () => {
    if (isLoading) return 'Loading...';
    if (isError) return `Couldn't load ${noun}`;
    return `No ${noun} found`;
  };

  const selectStyles = useMemo(() => {
    const base = Presentation.buildSelectStyles<EntityOption, false>(theme);
    return isSidePanel ? withSidePanelStyles(base) : base;
  }, [theme, isSidePanel]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  return (
    <Presentation.Row>
      <Presentation.Label>{label}</Presentation.Label>
      <EntityValue isSidePanel={isSidePanel}>
        <Select<EntityOption, false>
          aria-label={label}
          styles={selectStyles}
          theme={selectTheme}
          value={value}
          options={options}
          isClearable
          isSearchable
          isDisabled={disabled}
          isLoading={isLoading}
          placeholder={placeholder}
          noOptionsMessage={noOptionsMessage}
          onChange={(option) => onChange(option)}
          menuPlacement="auto"
          menuPortalTarget={isSidePanel ? document.body : undefined}
          menuPosition={isSidePanel ? 'fixed' : undefined}
        />
      </EntityValue>
    </Presentation.Row>
  );
}

const EntityValue = styled(Presentation.Value)<{ isSidePanel?: boolean }>(({ isSidePanel }) => ({
  minWidth: 0,
  overflow: isSidePanel ? 'hidden' : undefined,
}));
